import React, { useEffect, useState } from 'react';
import {
  Box,
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  IconButton,
  TextField,
  Typography,
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import { LEAD_LOST_REASONS, LEAD_LOST_REASON_LABELS } from '../leadsConfig';
import {
  LEADS_TEXT_SECONDARY,
  leadsDialogPaperSx,
  leadsDialogTitleSx,
  leadsGhostButtonSx,
  leadsPrimaryButtonSx,
  leadsTextFieldSx,
} from './leadsUi.styles';
import LeadInterestStockCheckbox from './LeadInterestStockCheckbox';
import { panelCloseIconButtonSx } from '../../Map/components/PanelShell/PanelHeader';

const reasonOptionSx = (active) => ({
  justifyContent: 'flex-start',
  textTransform: 'none',
  fontSize: '13px',
  fontWeight: active ? 600 : 500,
  borderRadius: '8px',
  px: 1.5,
  py: 1,
  border: '1px solid',
  borderColor: active ? '#E07A7A' : 'rgba(255,255,255,0.12)',
  bgcolor: active ? 'rgba(224,122,122,0.14)' : 'transparent',
  color: active ? '#FFFFFF' : '#D4D4D4',
  '&:hover': { borderColor: '#E07A7A', bgcolor: 'rgba(224,122,122,0.08)' },
});

/**
 * Pide el motivo de descarte de un interés (V3-3, `discard_reason`) y una nota
 * opcional. `onConfirm(reason, note)`.
 *
 * @param {{ open: boolean, itemLabel?: string, busy?: boolean,
 *   stockLabel?: string, onCancel: () => void,
 *   onConfirm: (reason: string, note: string, applyStock: boolean) => void }} props
 * `stockLabel` (salir de reservado / cerrado) → checkbox de stock destildado.
 */
export default function LeadLostReasonDialog({
  open, itemLabel = '', stockLabel = '', busy = false, onCancel, onConfirm,
}) {
  const [reason, setReason] = useState('');
  const [note, setNote] = useState('');
  const [applyStock, setApplyStock] = useState(false);

  useEffect(() => {
    if (open) { setReason(''); setNote(''); setApplyStock(false); }
  }, [open]);

  return (
    <Dialog
      open={open}
      onClose={busy ? undefined : onCancel}
      PaperProps={{ sx: { ...leadsDialogPaperSx, width: 'min(420px, 95vw)' } }}
      aria-labelledby="lead-lost-reason-title"
    >
      <DialogTitle id="lead-lost-reason-title" sx={leadsDialogTitleSx}>
        Descartar interés
        <IconButton size="small" onClick={onCancel} disabled={busy} aria-label="Cerrar" sx={panelCloseIconButtonSx}>
          <CloseIcon />
        </IconButton>
      </DialogTitle>
      <DialogContent sx={{ px: 2.5, pb: 1 }}>
        <Typography sx={{ color: LEADS_TEXT_SECONDARY, fontSize: '13px', mb: 1.5 }}>
          {itemLabel ? `¿Por qué se descarta ${itemLabel}?` : '¿Por qué se descarta este interés?'}
        </Typography>
        <Box role="radiogroup" aria-label="Motivo" sx={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 1 }}>
          {LEAD_LOST_REASONS.map((key) => (
            <Button
              key={key}
              role="radio"
              aria-checked={reason === key}
              onClick={() => setReason(key)}
              disabled={busy}
              sx={reasonOptionSx(reason === key)}
            >
              {LEAD_LOST_REASON_LABELS[key]}
            </Button>
          ))}
        </Box>
        <TextField
          label="Nota (opcional)"
          size="small"
          fullWidth
          multiline
          minRows={2}
          maxRows={5}
          value={note}
          onChange={(e) => setNote(e.target.value)}
          disabled={busy}
          inputProps={{ maxLength: 4000 }}
          sx={{ ...leadsTextFieldSx, mt: 2 }}
        />
        <LeadInterestStockCheckbox label={stockLabel} checked={applyStock} disabled={busy} onChange={setApplyStock} />
      </DialogContent>
      <DialogActions sx={{ px: 2.5, pb: 2, pt: 1.5, gap: 1 }}>
        <Button onClick={onCancel} disabled={busy} sx={leadsGhostButtonSx}>Cancelar</Button>
        <Button
          variant="contained"
          disabled={!reason || busy}
          onClick={() => onConfirm(reason, note.trim(), Boolean(stockLabel) && applyStock)}
          sx={{ ...leadsPrimaryButtonSx, bgcolor: '#C45C5C', '&:hover': { bgcolor: '#A84B4B', boxShadow: 'none' } }}
          startIcon={busy ? <CircularProgress size={14} sx={{ color: 'inherit' }} /> : null}
        >
          Descartar
        </Button>
      </DialogActions>
    </Dialog>
  );
}
